import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo } from '../components';
import { Button } from '../components';

/**
 * Forgot PIN Page
 * Allows users to request a PIN reset link via User ID
 */
const ForgotPin = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId.trim()) {
      toast.error('Please enter your User ID');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/auth/forgot-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        toast.success(result.message);
      } else {
        toast.error(result.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Forgot PIN Error:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F9FF] flex flex-col items-center justify-center px-4">
      {/* Brand Logo */}
      <Logo className="mb-8" />

      <div className="w-full max-w-[420px]">
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-8 sm:p-10">
          
          <button 
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-[13px] font-medium text-gray-500 hover:text-blue-600 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>

          {!isSubmitted ? (
            <>
              <div className="mb-8">
                <h2 className="text-[22px] font-bold text-gray-900 tracking-tight">Forgot PIN?</h2>
                <p className="text-[14px] text-gray-500 mt-1.5">Enter your User ID and we'll send a reset link to your registered email.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="userId" className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                    User ID
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                    <input
                      type="text"
                      id="userId"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="Enter your user ID"
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-[14px] text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  block
                  loading={isLoading}
                  rightIcon={<Send className="w-4 h-4" />}
                >
                  Send Reset Link
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-[20px] font-bold text-gray-900 mb-2">Check Your Email</h2>
              <p className="text-[14px] text-gray-500 leading-relaxed mb-8">
                If an account exists for <strong>{userId}</strong>, we've sent instructions to reset your PIN.
              </p>
              <Button
                variant="secondary"
                block
                onClick={() => navigate('/login')}
              >
                Return to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPin;
